import { createStore } from './createStore';
import { normalizeAssetImageFields } from '../utils/assetImageResolver';

/**
 * Project-level images store (Wave 6).
 *
 * The backend re-architecture made Image the canonical concept. This store
 * replaces `useProjectKeyframesStore` — but exposes BOTH the new `images` /
 * `imagesById` state keys and the legacy `keyframes` / `keyframesById`
 * aliases (pointing at the same arrays) so existing components keep working
 * while we migrate them one by one.
 *
 * Method aliases:
 *   setImages    ↔ setKeyframes
 *   upsertImage  ↔ upsertKeyframe
 *   deleteImage  ↔ deleteKeyframe
 *   getImageById ↔ getKeyframeById
 *   resetProjectImages ↔ resetProjectKeyframes
 *
 * Both old and new names hit the same underlying setter, and every setter
 * updates both `images[]` and `keyframes[]` with the same array reference so
 * either selector shape sees identical data.
 *
 * The old file `useProjectKeyframesStore.js` re-exports this module so no
 * import path breaks.
 */

const initialState = {
  images: [],
  imagesById: {},
  keyframes: [],         // legacy alias, same data as `images`
  keyframesById: {},     // legacy alias
};

function indexById(list) {
  const byId = {};
  list.forEach((img) => {
    if (img?.id) byId[img.id] = img;
  });
  return byId;
}

function refreshAssetImageUrls(imagesById) {
  import('./useAssetsStore').then(({ useAssetsStore }) => {
    useAssetsStore.setState((state) => ({
      assets: (state.assets || []).map((asset) => normalizeAssetImageFields(asset, imagesById)),
    }));
  }).catch(() => {});
}

export const useProjectImagesStore = createStore('ProjectImagesStore', (set, get) => {
  const setList = (list) => {
    const byId = indexById(list);
    refreshAssetImageUrls(byId);
    return {
      images: list,
      imagesById: byId,
      keyframes: list,
      keyframesById: byId,
    };
  };

  const upsertOne = (state, image) => {
    if (!image?.id) return state;
    const exists = state.images.some((k) => k.id === image.id);
    const nextList = exists
      ? state.images.map((k) => (k.id === image.id ? { ...k, ...image } : k))
      : [image, ...state.images];
    const nextById = {
      ...state.imagesById,
      [image.id]: { ...(state.imagesById[image.id] || {}), ...image },
    };
    refreshAssetImageUrls(nextById);
    return {
      images: nextList,
      imagesById: nextById,
      keyframes: nextList,
      keyframesById: nextById,
    };
  };

  const removeOne = (state, id) => {
    if (!id) return state;
    const nextList = state.images.filter((k) => k.id !== id);
    const nextById = { ...state.imagesById };
    delete nextById[id];
    refreshAssetImageUrls(nextById);
    return {
      images: nextList,
      imagesById: nextById,
      keyframes: nextList,
      keyframesById: nextById,
    };
  };

  return {
    ...initialState,

    // --- Canonical (images vocabulary) ---
    setImages: (images) => set(() => setList(Array.isArray(images) ? images.filter(Boolean) : [])),
    upsertImage: (image) => set((state) => upsertOne(state, image)),
    deleteImage: (id) => set((state) => removeOne(state, id)),
    resetProjectImages: () => set(() => initialState),
    getImageById: (id) => get().imagesById?.[id] || null,

    // --- Legacy aliases (same semantics, delegate to the canonical methods) ---
    setKeyframes: (keyframes) => set(() => setList(Array.isArray(keyframes) ? keyframes.filter(Boolean) : [])),
    upsertKeyframe: (keyframe) => set((state) => upsertOne(state, keyframe)),
    deleteKeyframe: (id) => set((state) => removeOne(state, id)),
    resetProjectKeyframes: () => set(() => initialState),
    getKeyframeById: (id) => get().imagesById?.[id] || null,
  };
});
